import React, {useEffect} from 'react';

import {skipToken} from '@reduxjs/toolkit/query';

import {DetailLoadingPage} from 'app/general/loading/DetailLoadingPage';
import {usePolicyByHashid, usePolicyVersions} from 'app/policies/api';
import {useProcedureByHashid, useProcedureVersions} from 'app/procedures/api';
import {useProcessByHashid, useProcessVersions} from 'app/process/api';
import {useQuiz, useQuizVersions} from 'app/quizzes/api';
import {appendPathToURL} from 'app/urls';
import {getLocation} from 'app/utils/urls';
import {goToDocumentEditPageRTK} from 'app/version/utils';

import type {PolicyRouterParamsType, PolicyRouteType} from 'app/policies/types';
import type {
  UseListQueryType,
  UseQueryType,
} from 'app/store/sweet-api/api-query';
import type {WithRouterProps} from 'react-router';
import type {QuizSerializedType, QuizVersionSerializedType} from 'sweet-types';

type ForwardToPolicyEditPageProps = WithRouterProps<PolicyRouterParamsType> & {
  route: PolicyRouteType;
};
export const ForwardToPolicyEditPage = ({
  params,
  location,
  router,
}: ForwardToPolicyEditPageProps) => {
  const {data: policy} = usePolicyByHashid(params.hashid);
  const {data: version} = usePolicyVersions(
    policy
      ? {
          objectId: policy.id,
          page: 1,
          page_size: '1',
        }
      : skipToken
  );
  useEffect(() => {
    if (policy != null && version != null && version.results.length > 0) {
      router.push(
        getLocation(
          appendPathToURL(
            version.results[0].html_url + location.search,
            'edit/'
          )
        )
      );
    }
  }, [policy, version]);
  return <DetailLoadingPage />;
};

export const ForwardToProcedureEditPage = ({
  params,
  location,
  router,
}: ForwardToPolicyEditPageProps) => {
  const {data: procedure} = useProcedureByHashid(params.hashid);
  const {data: version} = useProcedureVersions(
    procedure
      ? {
          objectId: procedure.id,
          page: 1,
          page_size: '1',
        }
      : skipToken
  );
  useEffect(() => {
    if (procedure != null && version != null && version.results.length > 0) {
      router.push(
        getLocation(
          appendPathToURL(
            version.results[0].html_url + location.search,
            'edit/'
          )
        )
      );
    }
  }, [procedure, version]);
  return <DetailLoadingPage />;
};
export const ForwardToProcessEditPage = ({
  params,
  location,
  router,
}: ForwardToPolicyEditPageProps) => {
  const {data: process} = useProcessByHashid(params.hashid);
  const {data: version} = useProcessVersions(
    process
      ? {
          objectId: process.id,
          page: 1,
          page_size: '1',
        }
      : skipToken
  );
  useEffect(() => {
    if (process != null && version != null && version.results.length > 0) {
      router.push(
        getLocation(
          appendPathToURL(
            version.results[0].html_url + location.search,
            'edit/'
          )
        )
      );
    }
  }, [process, version]);
  return <DetailLoadingPage />;
};

type RouterProps = WithRouterProps<{hashid: string}>;

type ForwardProps<T, V> = {
  useData: UseQueryType<string, T>;
  useLatestVersionData: UseListQueryType<any, V>;
};

// A generic page forwarding to /edit/ component. to be used by RTK query code.
const ForwardToDocumentEditPage = <
  T extends {id: number} | {id: string},
  V extends {html_url: string}
>({
  useData,
  useLatestVersionData,
  params,
  versionParam,
}: ForwardProps<T, V> &
  Pick<RouterProps, 'params'> & {versionParam: string}) => {
  const {data: object} = useData(params.hashid);
  const {version} = useLatestVersionData(
    {[versionParam]: object?.id},
    {
      skip: object?.id === null,
      selectFromResult: ({data}) => ({version: data?.results[0]}),
    }
  );
  useEffect(() => {
    if (version) {
      goToDocumentEditPageRTK({version});
    }
  }, [version]);
  return <DetailLoadingPage />;
};

export const ForwardToQuizEditPage = ({params}: RouterProps) => (
  <ForwardToDocumentEditPage<QuizSerializedType, QuizVersionSerializedType>
    useData={useQuiz}
    useLatestVersionData={useQuizVersions}
    params={params}
    versionParam="quizId"
  />
);
